/* ============================================================
   assets/bh-search.js
   Live search under the header field. No dependencies, ~3 KB.

   The theme's rule is one JavaScript file, bh-cod-form.js. This is
   the second, and it is deliberately kept apart from it: the order
   form is the thing that must never break, and a search box has no
   business sharing a file with it. Nothing here touches the form.

   Progressive enhancement, same as everything else:
     · no JS        → the form submits to /search and always has
     · JS           → suggestions under the field as you type
     · fetch fails  → the panel closes, the form still submits

   Results come back as rendered HTML from
   sections/bh-predictive-search.liquid, not as JSON, so prices and
   strings are formatted by Liquid exactly once.
   ============================================================ */
(function () {
  'use strict';

  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 220;
  var SECTION = 'bh-predictive-search';

  Array.prototype.forEach.call(document.querySelectorAll('[data-bh-search]'), setup);

  function setup(form) {
    var input = form.querySelector('.bh-search__input');
    var panel = form.querySelector('[data-bh-sr]');
    var status = form.querySelector('[data-bh-sr-status]');
    if (!input || !panel) return;

    var timer = null;
    var controller = null;
    var lastQuery = '';
    var active = -1;

    function options() {
      return panel.querySelectorAll('[role="option"]');
    }

    function close() {
      if (panel.hidden) return;
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      active = -1;
    }

    function open() {
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }

    /* aria-activedescendant moves the screen reader's focus without moving
       the real one — the caret has to stay in the field so the customer can
       keep typing. */
    function highlight(next) {
      var items = options();
      if (!items.length) return;
      if (active > -1 && items[active]) items[active].setAttribute('aria-selected', 'false');
      active = (next + items.length) % items.length;
      var el = items[active];
      el.setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', el.id);
      if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }

    /* The count and the word are kept apart rather than interpolated into
       a template in Liquid: a placeholder inside the output tag is one more
       thing to get wrong for a string nobody sees. */
    function announce(n) {
      if (!status) return;
      status.textContent = n + ' ' + (status.dataset.word || '');
    }

    function search(q) {
      /* An in-flight request for an older query would otherwise land after
         a newer one and repaint stale results. */
      if (controller) controller.abort();
      controller = 'AbortController' in window ? new AbortController() : null;

      /* The default searchable fields are title, product_type,
         variants.title and vendor — SKU is not among them. So typing a SKU
         returned nothing here while the results page, which searches
         everything, found the product straight away. Staff and repeat
         buyers type SKUs; the two had to agree.

         The defaults are listed explicitly because naming any field
         replaces the set rather than adding to it. */
      var FIELDS = [
        'title',
        'product_type',
        'variants.title',
        'vendor',
        'variants.sku',
        'variants.barcode',
      ].join(',');

      var url =
        '/search/suggest?q=' +
        encodeURIComponent(q) +
        '&section_id=' +
        SECTION +
        '&resources[type]=product&resources[limit]=6' +
        '&resources[options][fields]=' +
        encodeURIComponent(FIELDS);

      fetch(url, controller ? { signal: controller.signal } : undefined)
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        })
        .then(function (html) {
          /* The section renders inside a wrapper Shopify adds; take the
             inner block so the panel does not gain a stray div each time. */
          var tmp = document.createElement('div');
          tmp.innerHTML = html;
          var inner = tmp.querySelector('.bh-sr__inner');
          panel.innerHTML = inner ? inner.outerHTML : '';
          active = -1;
          input.removeAttribute('aria-activedescendant');
          open();
          announce(inner ? Number(inner.dataset.bhSrCount || 0) : 0);
        })
        .catch(function (err) {
          /* An abort is the expected outcome of typing quickly, not a
             failure — anything else just closes the panel and leaves the
             customer with the ordinary search form. */
          if (err && err.name === 'AbortError') return;
          close();
        });
    }

    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearTimeout(timer);
      if (q.length < MIN_CHARS) {
        lastQuery = '';
        close();
        return;
      }
      if (q === lastQuery) return;
      timer = setTimeout(function () {
        lastQuery = q;
        search(q);
      }, DEBOUNCE_MS);
    });

    input.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape') {
        close();
        return;
      }
      if (panel.hidden) return;

      if (ev.key === 'ArrowDown') {
        ev.preventDefault();
        highlight(active + 1);
      } else if (ev.key === 'ArrowUp') {
        ev.preventDefault();
        highlight(active - 1);
      } else if (ev.key === 'Enter') {
        var items = options();
        /* Enter with nothing highlighted must still submit the form — the
           customer who ignores the suggestions gets the results page. */
        if (active > -1 && items[active]) {
          ev.preventDefault();
          window.location.href = items[active].href;
        }
      }
    });

    /* Pointer down, not click: the panel is closed on blur, and a click
       would land after the blur has already removed the link. */
    panel.addEventListener('mousedown', function (ev) {
      var link = ev.target.closest ? ev.target.closest('[role="option"]') : null;
      if (link) {
        ev.preventDefault();
        window.location.href = link.href;
      }
    });

    input.addEventListener('focus', function () {
      if (panel.innerHTML.trim() && input.value.trim().length >= MIN_CHARS) open();
    });

    document.addEventListener('click', function (ev) {
      if (!form.contains(ev.target)) close();
    });

    form.addEventListener('submit', close);
  }
})();
